// The model's view of spec_read: each column and type, with 096's comment where it carries a vocabulary, unit or rule.
// Byte-stable for prompt caching; spec-read-ddl.test.ts fails if it drifts from the migration.
export const QUERY_DATA_PREAMBLE = [
  "Query Parcel's data with one PostgreSQL SELECT. This is the first call for any question about projects, accounts, contacts or signals.",
  "Tables are the views below. projects, accounts and contacts already carry the common joins (developer, architect, GC, counts, and this workspace's status, tags and notes); the workspace_ views hold only this workspace's saved records.",
  'Always select id when listing records, and lat, lng for projects, so present\'s Records can open them and pin them on its map; id must be a record view\'s own (a.id from accounts a), never a link column like account_id. To show a ranking number beside them, select it as a column and name it with count in present (count: "shared_projects").',
  'Up to 100 rows come back. When truncated is true, narrow the WHERE, aggregate with count(*) or group by, or page with offset 100. Rank with ORDER BY. Match names with ILIKE. An enum column takes only the values listed for it. Dates are timestamptz; last_signal_at says how fresh a project is.',
  "A series over time must fit in 100 rows, and signals reach back decades: bound the window (filed_at >= now() - interval '1 year' for weekly counts) or bucket by month or year.",
  "To rank by fit, join workspace_score_values (record_id = the record's id, score_key 'icp', 'atlas' or 'prospect') and order by score desc.",
  'Some words are refused anywhere in the SQL, even inside a string: copy, format, repeat, lpad, rpad and any pg_ name. Rephrase around them.',
  'Example, a ranked prospect list:',
  "select id, name, primary_address, city, residential_units, stories, stage, developer_name, architect_name, gc_status, workspace_status, last_signal_at, lat, lng from projects where state = 'MA' and primary_use = 'multifamily' and residential_units >= 100 and stories between 4 and 6 and gc_status <> 'named' order by residential_units desc limit 50",
].join('\n');

export const SPEC_READ_DDL = `-- view: projects
  id uuid -- Project id (uuid). Select it whenever you list projects.
  name text -- Project name; null when no source names it (use primary_address).
  primary_address text -- Street address as filed, e.g. '379 Totten Pond Rd'.
  additional_addresses text[] -- text[] of other addresses the project spans.
  city text -- City, e.g. 'Boston'. Match with ILIKE.
  neighborhood text
  state text -- Two-letter state code, e.g. 'MA'.
  zip text
  lat numeric -- Latitude; select lat and lng so the member can see projects on a map.
  lng numeric
  parent_project_id uuid -- Master plan id for a phase, else null; a master_plan row already counts its phases, so sum one or the other.
  project_classification enum -- single_building, master_plan or phased_development; leave master_plan rows out of sums, rankings and largest lists.
  uses text[] -- text[] of the uses filings name, lower-cased free text (e.g. residential, retail, lab); primary_use is the clean one.
  primary_use enum -- multifamily, mixed_use, office, industrial, retail, hotel, institutional, educational, r_and_d, other.
  stage enum -- pre_filing, filed, under_review, approved, permitted, under_construction, completed; null = unknown.
  stage_computed_at timestamptz
  source_phases jsonb -- jsonb: the raw status each source reported, keyed by source.
  description text
  project_website_url text
  external_ids jsonb -- jsonb of the project's ids in source systems (case and permit numbers), keyed by source.
  gross_floor_area_sf int -- Gross floor area in square feet; null when unknown.
  gross_floor_area_sf_is_estimated bool -- True when gross_floor_area_sf was estimated rather than reported.
  land_area_sf int
  residential_units int -- Dwelling units; null when unknown, never 0 for unknown.
  stories int -- Stories above grade; null when no source states one.
  height_ft int
  public_area_sf int
  cost_of_construction_usd bigint -- Construction cost in US dollars; cost_is_estimated says whether it was estimated.
  cost_is_estimated bool -- True when cost_of_construction_usd was estimated rather than reported.
  year_completed int
  year_completed_is_estimated bool -- True when year_completed is an expectation, not a fact.
  parking_spaces int -- Parking spaces; null when unknown.
  parking_type enum -- garage, underground, or surface.
  units_single_family int -- Single-family units (null = not reported; 0 is an observed zero).
  units_small_multifamily int
  units_large_multifamily int
  units_studio_or_1br int
  units_2br int
  units_3br_plus int
  affordable_units_0_30_ami int -- Affordable units at 0-30% of area median income.
  affordable_units_30_50_ami int
  affordable_units_50_80_ami int
  affordable_units_80_120_ami int
  affordable_units_unknown_ami int -- Affordable units whose AMI band is not stated.
  group_quarters_beds int -- Beds in group quarters (dorms, assisted living).
  hotel_rooms int
  jobs_estimated int
  jobs_reported int
  mixed_use bool
  age_restricted bool -- True for age-restricted (senior) housing.
  as_of_right bool -- True when the project needs no discretionary zoning relief.
  phased bool
  redevelopment bool
  stalled bool
  regulatory_flags text[] -- text[] of review paths, e.g. chapter_40b, special_permit, site_plan_review, variance, large_project_review.
  attribute_tags text[] -- text[]: gc_not_yet_selected, or structure_ + wood_frame, podium, steel, concrete, mass_timber, masonry, modular.
  traffic_count_url text
  nearest_transit text
  parcel_fiscal_year int
  first_signal_at timestamptz -- Often null; min(filed_at) over the project's signals is the reliable first date.
  last_signal_at timestamptz -- filed_at of the newest signal; how fresh Parcel's view of this project is.
  created_at timestamptz
  updated_at timestamptz
  geocode_status text -- Geocoding state: null, processing, applied, needs_review, manual.
  geocode_provenance jsonb -- jsonb: where lat/lng came from.
  location geometry -- PostGIS point (SRID 4326) built from lat/lng; prefer lat and lng.
  primary_address_normalized text -- Internal matching key for primary_address.
  additional_addresses_normalized text[] -- Internal matching keys for additional_addresses.
  disposition enum -- active, denied, withdrawn, expired, stalled, or cancelled.
  developer_id uuid
  developer_name text -- Name of the first developer only, or null (a project can have several); for a firm's projects, use project_accounts.
  architect_id uuid
  architect_name text -- Name of the first architect only, or null (a project can have several); for a firm's projects, use project_accounts.
  gc_id uuid
  gc_name text
  gc_status text -- named, not_yet_selected, or unknown.
  workspace_status text -- The member workspace's own status for this project (null when the workspace has not touched it).
  tags text[]
  notes text
  signals_count int -- Signals filed within the plan's history window.
  accounts_count int -- Distinct firms linked to the project, in any role.
  contacts_count int -- Distinct people linked to the project, in any role.
  lead_media_id uuid
-- view: accounts
  id uuid -- Account id (uuid). Select it whenever you list accounts.
  name text -- Firm name. Match with ILIKE.
  aliases text[]
  domains text[]
  website text
  linkedin_url text
  city text
  state text
  parent_account_id uuid -- Parent firm's account id, else null.
  first_seen_signal_id uuid
  created_at timestamptz
  updated_at timestamptz
  account_types enum[]
  account_types_source enum -- resolved (from filings) or enriched (classified from the web).
  account_types_enriched_at timestamptz
  hq_address text
  public_phone text
  public_email text
  specialties text[]
  description text
  founded_year int
  hq_lat float8
  hq_lng float8
  hq_geocoded_address text
  projects_count int -- Projects the firm is linked to, in any role.
  workspace_status text -- The member workspace's own status for this firm, or null.
  tags text[]
  notes text
-- view: contacts
  id uuid -- Contact id (uuid). Select it whenever you list contacts.
  account_id uuid -- The firm the contact works at (accounts.id).
  name text -- Full name. Match with ILIKE.
  title text
  email text -- Work email, when known.
  email_verified_at timestamptz -- When the email was last verified; null = unverified.
  linkedin_url text
  enrichment_status enum -- enriched, partial, unenriched, or stale.
  enrichment_attempted_at timestamptz
  created_at timestamptz
  updated_at timestamptz
  phone text
  account_name text
  projects_count int -- Projects the contact is linked to.
  workspace_status text -- The member workspace's own status for this contact, or null.
  tags text[]
  notes text
  revealed_kinds text[] -- What this workspace unlocked: email, phone, profile.
  revealed_email text -- This workspace's best unlocked email; email is the public one.
  revealed_email_status enum -- verified, probable, catch_all or unverified.
-- view: signals
  id uuid
  project_id uuid -- The project (projects.id).
  signal_type enum
  track enum -- funnel (moves the project's stage) or non_funnel.
  filed_at timestamptz -- When the event happened; rank recency by this.
  date_precision text -- How exact filed_at is: exact, month, year, unknown; null on older rows.
  discovered_at timestamptz
  summary text
  reported_cost_usd bigint -- Cost the filing reported, in US dollars.
  reported_gross_floor_area_sf int -- Gross floor area the filing reported, in square feet.
  reported_units int -- Dwelling units the filing reported.
  created_at timestamptz
-- view: project_accounts
  project_id uuid -- The project (projects.id).
  account_id uuid -- The firm (accounts.id).
  role enum -- developer, owner, architect, gc, engineer, other, law_firm, consultant, landscape_architect.
  specific_role enum
  source enum -- scraped or user_added.
  extracted_signal_id uuid
  created_at timestamptz
-- view: project_contacts
  project_id uuid -- The project (projects.id).
  contact_id uuid -- The contact (contacts.id).
  role enum -- developer_contact, architect_contact, engineer_contact, gc_contact, agency_pm, attorney, owner_contact, other.
  source enum -- scraped or user_added.
  extracted_signal_id uuid
  created_at timestamptz
-- view: workspace_projects
  id uuid
  workspace_id uuid -- The member's workspace.
  project_id uuid -- The project (projects.id).
  workspace_status text -- The workspace's own status, free text.
  notes text
  tags text[]
  created_by uuid
  created_at timestamptz
  updated_at timestamptz
-- view: workspace_accounts
  id uuid
  workspace_id uuid -- The member's workspace.
  account_id uuid -- The firm (accounts.id).
  workspace_status text -- The workspace's own status, free text.
  notes text
  tags text[]
  created_by uuid
  created_at timestamptz
  updated_at timestamptz
-- view: workspace_contacts
  id uuid
  workspace_id uuid -- The member's workspace.
  contact_id uuid -- The contact (contacts.id).
  workspace_status text -- The workspace's own status, free text.
  notes text
  tags text[]
  created_by uuid
  created_at timestamptz
  updated_at timestamptz
-- view: workspace_score_values
  record_id uuid -- The record: accounts.id (icp), projects.id (atlas) or contacts.id (prospect).
  score_key text -- icp (ICP Score, accounts), atlas (Atlas Score, projects) or prospect (Prospect Score, contacts).
  score int -- 0-100, higher is a better fit; 0 when a disqualify criterion matched.
  bucket text -- icp: tier_1,tier_2,tier_3,disqualified; atlas: priority,secondary,watch,pass; prospect: excellent,good,fair,not_a_fit.
  bucket_label text -- The workspace's name for the bucket.
  matched jsonb -- jsonb array of the criterion ids that matched; explain a bucket from these.
-- values:
--   accounts.account_types: developer, architect, gc, engineer, agency, law_firm, consultant, landscape_architect
--   signals.signal_type: project_announcement, pre_application, site_acquisition, demolition_permit, site_prep, entitlement_application, environmental_review, public_review, rfp, entitlement_approval, entitlement_denial, withdrawal, expiration, permit_filed, permit_issued, construction_loan, under_construction, team_update, completion
--   project_accounts.specific_role: landscape_architect, architect_of_record, design_architect, civil_engineer, structural_engineer, mep_engineer, mechanical_engineer, electrical_engineer, plumbing_engineer, geotechnical_engineer, traffic_engineer, transportation_engineer, surveyor, wind_engineer, permitting_consultant, environmental_consultant, planning_consultant, sustainability_consultant, historic_consultant`;

export const QUERY_DATA_SQL_DESCRIPTION = `${QUERY_DATA_PREAMBLE}\n\n${SPEC_READ_DDL}`;
